import { useEffect, useState } from "react";
import { codeToHtml } from "shiki";

interface HighlightedCodeProps {
  code: string;
  /** Shiki language identifier — ts, python, go, json, bash, etc. */
  lang?: string;
  /** Theme to use — defaults to a deep dark IDE theme. */
  theme?: string;
  className?: string;
}

/**
 * Renders `code` syntax-highlighted with Shiki. Highlights on mount;
 * shows the raw text in a darkened style while loading so there's no
 * layout shift.
 */
export function HighlightedCode({
  code,
  lang = "ts",
  theme = "dark-plus",
  className,
}: HighlightedCodeProps) {
  const [html, setHtml] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    codeToHtml(code, { lang, theme })
      .then((out) => {
        if (!cancelled) setHtml(out);
      })
      .catch(() => {
        if (!cancelled) setHtml(null);
      });
    return () => {
      cancelled = true;
    };
  }, [code, lang, theme]);

  if (!html) {
    return (
      <pre
        className={
          "overflow-x-auto p-5 text-[13px] leading-relaxed text-zinc-200 " +
          (className ?? "")
        }
      >
        <code>{code}</code>
      </pre>
    );
  }

  return (
    <div
      className={
        "shiki-out [&_pre]:!m-0 [&_pre]:!bg-transparent [&_pre]:overflow-x-auto [&_pre]:p-5 [&_pre]:text-[13px] [&_pre]:leading-relaxed " +
        (className ?? "")
      }
      // Shiki output is trusted — we generated it from our own static
      // source string with a built-in language + theme.
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
